import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ReadonlySectionHeader} from '@github-ui/issue-metadata/ReadonlySectionHeader'
import {Section} from '@github-ui/issue-metadata/Section'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {Button} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {Suspense, useState} from 'react'

import {LABELS} from '../../constants/labels'
import type {IssueSecondaryNode} from '../issue-viewer-secondary-query-types'
import {Participant} from './Participant'
import type {IssueSecondaryRepositoryNode, IssueSecondaryRepositoryOwnerNode} from './participants-section-query-types'
import {ParticipantsList} from './ParticipantsList'
import {ParticipantsListLoading} from './ParticipantsListLoading'
import styles from './ParticipantsSection.module.css'

// Parent (`IssueSidebarLazySections`) still reads this via Relay, so widen the Pick with `RelayCompat`
// to accept Relay data (`| null` widened to `| null | undefined`). Remove once the parent migrates.
// The generated `repository`/`owner` types are the union of every fragment's selection in the secondary
// query, so we Pick at each level (issue, repository, owner) to read just the fields this component uses.
type ParticipantsSectionFragment = RelayCompat<
  Pick<
    IssueSecondaryNode,
    | 'number'
    | 'participants'
    | 'viewerCanConvertToDiscussion'
    | 'viewerCanDelete'
    | 'viewerCanTransfer'
    | 'viewerCanType'
    | 'viewerCanLock'
  > & {
    readonly repository: Pick<IssueSecondaryRepositoryNode, 'name' | 'viewerCanPinIssues'> & {
      readonly owner: Pick<IssueSecondaryRepositoryOwnerNode, 'login'>
    }
  }
>

type ParticipantsSectionProps = {
  issue: ParticipantsSectionFragment
}

export function ParticipantsSection({issue}: ParticipantsSectionProps) {
  const {
    repository: {
      owner: {login: owner},
      name: repo,
      viewerCanPinIssues,
    },
    number,
    participants,
    viewerCanConvertToDiscussion,
    viewerCanDelete,
    viewerCanTransfer,
    viewerCanType,
    viewerCanLock,
  } = issue

  const showCopilotAppOpenAction = !isFeatureEnabled('issues_disable_copilot_app_open_action')
  const hideDivider =
    !viewerCanLock &&
    !viewerCanTransfer &&
    !viewerCanDelete &&
    !viewerCanPinIssues &&
    !viewerCanConvertToDiscussion &&
    !viewerCanType &&
    !showCopilotAppOpenAction

  const filteredParticipants = (participants.nodes || []).flatMap(a => (a ? a : [])).slice(0, 5)
  const [isOpen, setIsOpen] = useState(false)

  const hasParticipants = filteredParticipants.length > 0
  const showMoreCount = participants.totalCount - 5
  const showMoreAriaLabel = showMoreCount
    ? `and ${showMoreCount} other participants, click to see the full list`
    : undefined

  return (
    <Section
      sectionHeader={<ReadonlySectionHeader title={LABELS.sectionTitles.participants} />}
      emptyText={hasParticipants ? undefined : LABELS.emptySections.participants}
      hideDivider={hideDivider}
    >
      <div data-testid="sidebar-participants-section" className={styles.participantsContainer}>
        <div className={styles.participantsListContainer}>
          {filteredParticipants.map(participant => (
            <Participant key={participant.id} participant={participant} />
          ))}
        </div>
        {showMoreCount > 0 && (
          <Button
            aria-label={showMoreAriaLabel}
            variant="invisible"
            onClick={() => setIsOpen(true)}
            className={styles.showMoreButton}
          >
            +{showMoreCount}
          </Button>
        )}
        {isOpen && (
          <Dialog title="Participants" onClose={() => setIsOpen(false)}>
            <div className={styles.participantsDialogContent}>
              <Suspense fallback={<ParticipantsListLoading />}>
                <ParticipantsList owner={owner} repo={repo} number={number} totalCount={participants.totalCount} />
              </Suspense>
            </div>
          </Dialog>
        )}
      </div>
    </Section>
  )
}
